import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

import HaciendaInvalidationLogService from "../../services/hacienda/HaciendaInvalidationLogService";

import {
    setPaginatedList,
    setSelectedLog,
    setCloseDetailModal,
    setOpenDetailModal,
    setPageSize,
    setIsLoading,
} from "../../store/slices/hacienda/dteInvalidationLogSlice";
import { DteInvalidationLogFilter } from "../../types/hacienda/DteInvalidationLogFilter";

export const useDteInvalidationLogs = () => {
    const dispatch: AppDispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    const {
        paginatedList,
        selectedLog,
        paginator,
        pageSize,
        showDetailModal,
        isLoading,
    } = useSelector((state: RootState) => state.dteInvalidationLog);

    /**
  * 📄 Filtra errores de invalidación con paginación
  *
  * 🔍 Realiza una solicitud POST al backend enviando filtros,
  * paginación y ordenamiento.
  *
  * @param filters - Filtros aplicados (ej. generationCode).
  * @param page - Página actual.
  * @param size - Tamaño de página.
  * @param sort - Ordenamiento (por defecto: createdAt,desc).
  */
    const handlerGetSearch = async (
        filters: DteInvalidationLogFilter,
        page = 0,
        size = pageSize,
        sort = "createdAt,desc"
    ) => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response = await HaciendaInvalidationLogService.getSearch(filters, page, size, sort);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setPaginatedList(data));

            } else {
                showAlert(
                    "error",
                    "Error",
                    "No se pudieron obtener los registros."
                );
            }
        } catch (error) {
            handle(error);
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };


    /**
     * 🔎 Obtener detalle por ID
     */
    const handlerGetById = async (id: number) => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response =
                await HaciendaInvalidationLogService.getById(id);

            console.log(response);

            const { statusCode, data, title, message, type } = response;

            if (statusCode === 200 && data) {
                dispatch(setSelectedLog(data));
                dispatch(setOpenDetailModal());
            } else {
                showAlert(
                    type || "error",
                    title || "Error",
                    message || "No se pudo obtener el detalle."
                );
            }
        } catch (error) {
            handle(error);
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };

    /**
     * ❌ Cerrar modal de detalle
     */
    const closeDetailModal = () => {
        dispatch(setCloseDetailModal());
    };

    /**
     * 📥 Descargar JSON enviado
     */
    const handlerDownloadRequestJson = async (id: number) => {
        try {
            showLoadingIndicator();

            await HaciendaInvalidationLogService.downloadRequestJson(id);

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 📥 Descargar JSON respuesta
     */
    const handlerDownloadResponseJson = async (id: number) => {
        try {
            showLoadingIndicator();

            await HaciendaInvalidationLogService.downloadResponseJson(id);

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 📄 Cambiar tamaño de página
     */
    const updatePageSize = (size: number) => {
        dispatch(setPageSize(size));
    };

    return {
        paginatedList,
        selectedLog,
        paginator,
        pageSize,
        showDetailModal,
        isLoading,

        handlerGetSearch,
        handlerGetById,
        closeDetailModal,
        handlerDownloadRequestJson,
        handlerDownloadResponseJson,
        updatePageSize,
    };
};
